/**
 * THE settings store — §14.1, §10.3.
 *
 * ONE persisted store for every user preference in the reader, assembled from four
 * slices that live in their own files: `./display.ts`, `./audio.ts`, `./repeat.ts`,
 * `./english.ts`.
 * Owner of all of them; nothing else writes any of these fields (§4.1).
 *
 * Two things are assembled HERE because they belong to no single slice:
 *
 *  1. `audioEpoch` IS THE RESTART SIGNAL, and audio AND repeat both bump it. So it is
 *     root state, and `edit()` — the helper that applies a patch and bumps it — is
 *     built here and handed to both slice factories. IT IS DELIBERATELY NOT PERSISTED:
 *     it is a session nonce, and a value restored from localStorage would force a
 *     spurious seek on the first reconcile. See `audio/AudioEngine.tsx` for why this is
 *     an epoch rather than a subscription: the element has exactly one writer, and this
 *     keeps it that way.
 *
 *  2. REHYDRATION SEEDS THE REDUCER'S REPEAT MIRROR (A19). It runs AFTER the store
 *     exists, so it cannot be done at module scope, and a payload written before a
 *     field existed carries no repeat counts, so the slice defaults must win.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LS_SETTINGS, SETTINGS_VERSION } from '@mushaf/corpus'
import { applyTheme, createDisplaySlice } from './display'
import { createAudioSlice } from './audio'
import { createRepeatSlice, pushRepeat } from './repeat'
import { createEnglishSlice } from './english'
import { partializeSettings, settingsStorage } from './storage'
import type { SettingsState } from './types'

export type { ThemeId, DisplayMode, TranslitScheme, SettingsState } from './types'
export { THEMES, DISPLAY_MODES } from './display'
export { TRANSLIT_OPTIONS } from './english'
export {
  ENGLISH_SCALE_DEFAULT,
  ENGLISH_SCALE_MAX,
  ENGLISH_SCALE_MIN,
  ENGLISH_SCALE_STEP,
} from './english'

export const useSettings = create<SettingsState>()(
  persist(
    (set, get) => {
      /**
       * The restart signal, in one place. Display settings deliberately do NOT use it.
       */
      const edit = (patch: Partial<SettingsState>) =>
        set({ ...patch, audioEpoch: get().audioEpoch + 1 })

      return {
        audioEpoch: 0,
        ...createDisplaySlice(set),
        ...createAudioSlice(edit),
        ...createRepeatSlice(edit),
        // `set`, not `edit`: changing translation must not restart the recitation.
        ...createEnglishSlice(set),
      }
    },
    {
      name: LS_SETTINGS,
      version: SETTINGS_VERSION,
      storage: settingsStorage,
      partialize: partializeSettings,
      onRehydrateStorage: () => (state) => {
        if (!state) return
        pushRepeat({ perAyah: state.repeatAyah, perPassage: state.repeatPassage })
      },
    },
  ),
)

/**
 * Applied at module scope so the attribute is on <html> before React mounts. The
 * inline script in index.html does the same thing one step earlier, for the very
 * first paint; this keeps the two in sync after hydration.
 */
applyTheme(useSettings.getState().theme)

/** Selectors — components subscribe to the narrowest slice they need. */
export const selectAudioEpoch = (s: SettingsState) => s.audioEpoch
